import { useLayoutEffect, useRef, useState } from 'react';

let nextAnimatedItemId = 0;
const createItemId = () => `drag-item-${++nextAnimatedItemId}`;

/**
 * Drag-to-reorder with live reordering and FLIP-based slide animations.
 *
 * Keeps a stable id per list position (spliced in sync with reorders) so
 * items can be keyed by identity, letting React move DOM nodes and letting
 * us animate the position deltas.
 */
export function useAnimatedDragReorder(
  itemCount: number,
  reorder: (_fromIndex: number, _toIndex: number) => boolean,
) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const idsRef = useRef<string[]>([]);
  const elementsRef = useRef(new Map<string, HTMLElement>());
  const prevTopsRef = useRef(new Map<string, number>());

  // Keep exactly one stable id per item. Adds/removes just extend or trim
  // the list; only reorders (which preserve length) need id continuity.
  if (idsRef.current.length < itemCount) {
    idsRef.current = [
      ...idsRef.current,
      ...Array.from(
        { length: itemCount - idsRef.current.length },
        createItemId,
      ),
    ];
  } else if (idsRef.current.length > itemCount) {
    idsRef.current = idsRef.current.slice(0, itemCount);
  }
  const ids = idsRef.current;

  useLayoutEffect(() => {
    const prevTops = prevTopsRef.current;
    const nextTops = new Map<string, number>();
    elementsRef.current.forEach((element, id) => {
      // offsetTop ignores in-flight transform animations, unlike
      // getBoundingClientRect, so rapid consecutive reorders stay accurate.
      nextTops.set(id, element.offsetTop);
    });
    const draggedId = draggedIndex === null ? null : ids[draggedIndex];
    nextTops.forEach((top, id) => {
      // The dragged card snaps into place; only its neighbors slide.
      if (id === draggedId) return;
      const prevTop = prevTops.get(id);
      if (prevTop === undefined) return;
      const deltaY = prevTop - top;
      if (Math.abs(deltaY) < 1) return;
      const element = elementsRef.current.get(id);
      if (!element) return;
      element.getAnimations().forEach((animation) => animation.cancel());
      element.animate(
        [
          { transform: `translateY(${deltaY}px)` },
          { transform: 'translateY(0px)' },
        ],
        { duration: 180, easing: 'ease-in-out' },
      );
    });
    prevTopsRef.current = nextTops;
  });

  const handleDragStart = (event: React.DragEvent, index: number) => {
    event.dataTransfer.setData('text/plain', String(index));
    event.dataTransfer.effectAllowed = 'move';
    setDraggedIndex(index);
  };

  const handleDragOver = (event: React.DragEvent, index: number) => {
    event.preventDefault();
    if (
      draggedIndex === null ||
      draggedIndex === index ||
      draggedIndex < 0 ||
      index < 0 ||
      draggedIndex >= ids.length ||
      index >= ids.length
    ) {
      return;
    }

    // Only swap once the cursor crosses the midpoint of the hovered card.
    // Without this, hovering near a boundary (or the card sliding through
    // the cursor mid-animation) retriggers swaps and the list jitters.
    const target = elementsRef.current.get(ids[index]);
    if (target) {
      // While a card is still sliding, its visual position sweeps under the
      // cursor; acting on it would swap back and forth. Wait it out.
      if (target.getAnimations().length > 0) return;
      const rect = target.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      const movingDown = index > draggedIndex;
      if (movingDown ? event.clientY < midY : event.clientY > midY) {
        return;
      }
    }

    if (!reorder(draggedIndex, index)) return;
    const [movedId] = ids.splice(draggedIndex, 1);
    ids.splice(index, 0, movedId);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const removeItem = (index: number) => {
    if (index < 0 || index >= idsRef.current.length) return false;
    idsRef.current.splice(index, 1);
    setDraggedIndex((current) => {
      if (current === null) return null;
      if (current === index) return null;
      return current > index ? current - 1 : current;
    });
    return true;
  };

  const registerItem = (id: string) => (element: HTMLElement | null) => {
    if (element) {
      elementsRef.current.set(id, element);
    } else {
      elementsRef.current.delete(id);
    }
  };

  return {
    ids,
    draggedIndex,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    removeItem,
    registerItem,
  };
}
